import { useState, useEffect } from 'react'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

const pingApi = async (): Promise<boolean> => {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)
    
    const res = await fetch(`${BASE_URL}/health`, {
      method: 'GET',
      signal: controller.signal,
    })
    
    clearTimeout(timeoutId)
    return res.status >= 100 && res.status < 600
  } catch {
    return false
  }
}

// Singleton state
let globalIsOnline = typeof navigator !== 'undefined' ? navigator.onLine : true
let globalIsChecking = false
const listeners = new Set<(state: { isOnline: boolean; isChecking: boolean }) => void>()

const notifyAll = () => {
  listeners.forEach(listener => listener({ isOnline: globalIsOnline, isChecking: globalIsChecking }))
}

const updateState = (isOnline: boolean, isChecking: boolean) => {
  globalIsOnline = isOnline
  globalIsChecking = isChecking
  notifyAll()
}

// Set up window listeners once
if (typeof window !== 'undefined') {
  const handleOnline = async () => {
    updateState(globalIsOnline, true)
    const hasInternet = await pingApi()
    updateState(hasInternet, false)
  }

  const handleOffline = () => {
    updateState(false, false)
  }

  const handleAppOffline = () => {
    updateState(false, false)
  }

  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  window.addEventListener('app:offline', handleAppOffline)
}

export const useConnectionStatus = () => {
  const [state, setState] = useState({ isOnline: globalIsOnline, isChecking: globalIsChecking })

  useEffect(() => {
    const listener = (newState: { isOnline: boolean; isChecking: boolean }) => {
      setState(newState)
    }
    listeners.add(listener)
    setState({ isOnline: globalIsOnline, isChecking: globalIsChecking })
    return () => {
      listeners.delete(listener)
    }
  }, [])

  return state
}
